import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  getElementById,
  getContainerClass,
  getLabelStyles,
} from "../utils/styleUtils";

export const CustomFileInput = ({ instanceId, field, form, ...props }) => {
  const formBuilder = useSelector((state) => state.customFormBuilder);
  const [fileInfo, setFileInfo] = useState({
    name: "",
    size: 0,
    preview: null,
  });
  const [isDragging, setIsDragging] = useState(false);

  const element = getElementById(instanceId, formBuilder);

  if (!element) {
    return <div>File input element not found</div>;
  }

  const elementStyle = element.style || {};
  const labelPosition = elementStyle.labelPosition || "top";

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setFileInfo({ name: "", size: 0, preview: null });
      if (field && form) {
        form.setFieldValue(field.name, null);
      }
      return;
    }

    // Create preview for image files if showPreview is enabled
    let preview = null;
    if (elementStyle.showPreview && file.type.startsWith("image/")) {
      preview = URL.createObjectURL(file);
    }

    setFileInfo({
      name: file.name,
      size: file.size,
      preview: preview,
    });

    // Update form field value
    if (field && form) {
      form.setFieldValue(field.name, file);
    }
  };

  const getInputContainerStyles = () => {
    return {
      border: `2px ${elementStyle.borderStyle || "dashed"} ${
        isDragging
          ? elementStyle.focusBorderColor || "#0d6efd"
          : elementStyle.borderColor || "#ced4da"
      }`,
      borderRadius: elementStyle.borderRadius || "0.375rem",
      padding: elementStyle.padding || "1.5rem",
      backgroundColor: isDragging
        ? elementStyle.hoverBackgroundColor || "rgba(13, 110, 253, 0.05)"
        : elementStyle.backgroundColor || "#f8f9fa",
      width: "100%",
      textAlign: "center",
      cursor: "pointer",
      transition: "all 0.2s ease",
    };
  };

  const getButtonStyles = () => {
    return {
      backgroundColor: elementStyle.buttonColor || "#0d6efd",
      color: elementStyle.buttonTextColor || "#ffffff",
      border: "none",
      borderRadius: "0.25rem",
      padding: "0.375rem 0.75rem",
      fontSize: "0.875rem",
      cursor: "pointer",
      marginTop: "0.75rem",
      marginBottom: "0.5rem",
      transition: "background-color 0.15s ease-in-out",
    };
  };

  const getFileInfoStyles = () => {
    return {
      marginTop: "0.5rem",
      fontSize: elementStyle.infoFontSize || "0.75rem",
    };
  };

  const getPreviewStyles = () => {
    return {
      maxWidth: "100%",
      maxHeight: elementStyle.previewHeight || "200px",
      marginTop: "0.75rem",
      borderRadius: "0.25rem",
      border: "1px solid #ced4da",
      boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)",
    };
  };

  // Get accepted file types
  const getAcceptedFileTypes = () => {
    // Default to common files if not specified
    if (
      !elementStyle.acceptedFileTypes ||
      elementStyle.acceptedFileTypes.length === 0
    ) {
      return undefined; // Browser will accept all files
    }

    // Join the file types with commas
    return elementStyle.acceptedFileTypes.join(",");
  };

  const fileTypesText = elementStyle.acceptedFileTypes
    ? `Accepted files: ${elementStyle.acceptedFileTypes.join(", ")}`
    : "All file types accepted";

  const maxFileSize = elementStyle.maxFileSize || "Unlimited";
  const maxFileSizeMessage =
    maxFileSize !== "Unlimited" ? `Max size: ${maxFileSize}MB` : "";

  return (
    <div className={getContainerClass(labelPosition)} style={{ width: "100%" }}>
      {elementStyle.label?.label && (
        <label
          htmlFor={`file-input-${instanceId}`}
          style={getLabelStyles(elementStyle, labelPosition)}
        >
          {elementStyle.label.label}
          {elementStyle.isRequired && (
            <span style={{ color: "#dc3545", marginLeft: "0.2rem" }}>*</span>
          )}
        </label>
      )}

      <div className="d-flex flex-column w-100">
        <label
          htmlFor={`file-input-${instanceId}`}
          style={getInputContainerStyles()}
          className="file-drop-zone"
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsDragging(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);

            const dt = e.dataTransfer;
            const files = dt.files;

            if (files.length) {
              // Simulate file input change
              const input = document.getElementById(`file-input-${instanceId}`);
              if (input) {
                Object.defineProperty(input, "files", {
                  value: files,
                });
                input.dispatchEvent(new Event("change", { bubbles: true }));
              }
            }
          }}
        >
          <input
            id={`file-input-${instanceId}`}
            type="file"
            accept={getAcceptedFileTypes()}
            onChange={handleFileChange}
            required={elementStyle.isRequired}
            className="d-none" // Hide the actual input
            {...props}
          />
          <div>
            <i
              className="bi bi-cloud-arrow-up"
              style={{ fontSize: "2rem", color: "#6c757d" }}
            ></i>
            <div style={{ marginTop: "0.75rem" }}>
              {fileInfo.name ? (
                <>
                  <strong>{fileInfo.name}</strong>
                  <div style={{ fontSize: "0.75rem", color: "#6c757d" }}>
                    {(fileInfo.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                </>
              ) : (
                <>
                  <div style={{ marginBottom: "0.5rem" }}>
                    {elementStyle.dropzoneText ||
                      "Drag & drop a file or click to browse"}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      document
                        .getElementById(`file-input-${instanceId}`)
                        .click()
                    }
                    style={getButtonStyles()}
                  >
                    Browse Files
                  </button>
                </>
              )}
            </div>
          </div>
        </label>

        {/* Preview image if available and enabled */}
        {elementStyle.showPreview && fileInfo.preview && (
          <div className="text-center">
            <img
              src={fileInfo.preview}
              alt="File preview"
              style={getPreviewStyles()}
            />
          </div>
        )}

        {/* File info and restrictions */}
        <div
          className="text-muted d-flex justify-content-between"
          style={getFileInfoStyles()}
        >
          <small>{fileTypesText}</small>
          {maxFileSizeMessage && <small>{maxFileSizeMessage}</small>}
        </div>

        {/* Button to clear selection */}
        {fileInfo.name && (
          <button
            type="button"
            className="btn btn-sm mt-2"
            onClick={() => {
              // Clear the file input
              const fileInput = document.getElementById(
                `file-input-${instanceId}`
              );
              if (fileInput) {
                fileInput.value = "";
                fileInput.dispatchEvent(new Event("change", { bubbles: true }));
              }
            }}
            style={{
              fontSize: "0.75rem",
              color: "#6c757d",
              background: "none",
              border: "1px solid #ced4da",
              borderRadius: "0.25rem",
              padding: "0.25rem 0.5rem",
            }}
          >
            <i className="bi bi-x-circle me-1"></i> Remove File
          </button>
        )}
      </div>
    </div>
  );
};
